import api from "../../api/axios";
import endpoints from "../../api/endpoints";

export type LoadValueType = "REGION" | "AREA" | "CENTER" | "LOCATION" | "COST_CENTER";
export type SortByType = "DEPTID" | "DEPTNAME";

interface LocationParams {
  loadValue: LoadValueType;
  regionCode?: string;
  areaCode?: string;
  serviceAreaCode?: string;
  entityCode?: string;
  centerCode?: string;
  locationCode?: string;
  sortBy?: SortByType;
}

interface LocationResponse {
  data: any[];
  status: string;
  message?: string;
}

/**
 * Fetches location hierarchy data from the UBT locations API
 * @param params - Location parameters including loadValue and optional filter codes
 * @returns Promise with location data
 */
export const fetchLocations = async (params: LocationParams): Promise<any> => {
  const queryParams = new URLSearchParams();
  
  queryParams.append("loadValue", params.loadValue);
  
  if (params.regionCode) queryParams.append("regionCode", params.regionCode);
  if (params.areaCode) queryParams.append("areaCode", params.areaCode);
  if (params.serviceAreaCode) queryParams.append("serviceAreaCode", params.serviceAreaCode);
  if (params.entityCode) queryParams.append("entityCode", params.entityCode);
  if (params.centerCode) queryParams.append("centerCode", params.centerCode);
  if (params.locationCode) queryParams.append("locationCode", params.locationCode);
  if (params.sortBy) queryParams.append("sortBy", params.sortBy);

  const response = await api.get<LocationResponse>(`/ubt/locations?${queryParams.toString()}`);
  return response.data;
};

interface SearchUBTParams {
  page: number;
  perPage: number;
  sortBy?: string;
  sortOrder?: "ASC" | "DESC";
  searchBy?: string;
  searchText?: string;
  fromId?: string;
  toId?: string;
  regionCode?: string;
  entityCode?: string;
  areaCode?: string;
  centerCode?: string;
  locationCode?: string;
  costCenterIds?: (string| number)[];
  countOnly?: boolean; // Optional parameter to indicate if only count is needed for pagination
}

/**
 * Fetches UBT list with search and filter parameters
 * @param params - Search parameters
 * @returns Promise with UBT list data
 */
export const fetchUBTList = async (params: SearchUBTParams): Promise<any> => {
  const queryParams = new URLSearchParams();

  queryParams.append("page", params.page.toString());
  queryParams.append("per_page", params.perPage.toString());

  // fromId/toId are only valid alongside searchBy=UBTId; the BFF returns 400 otherwise.
  const isUbtIdSearch = params.searchBy === "UBTId";

  // Map of optional query params to their API key. Undefined/empty values are skipped.
  const optionalParams: Array<[string | undefined, string]> = [
    [params.sortBy, "sortColumn"],
    [params.sortOrder, "sortOrder"],
    [params.searchBy, "searchBy"],
    [params.searchText, "searchString"],
    [isUbtIdSearch ? params.fromId : undefined, "fromId"],
    [isUbtIdSearch ? params.toId : undefined, "toId"],
    [params.regionCode, "regionCode"],
    [params.entityCode, "entityCode"],
    [params.areaCode, "areaCode"],
    [params.centerCode, "serviceAreaCode"],
    [params.locationCode, "locationCode"],
  ];
  optionalParams.forEach(([value, key]) => {
    if (value) queryParams.append(key, value);
  });

  if (params.costCenterIds && params.costCenterIds.length > 0) {
    if (params.costCenterIds.includes("All")) {
      queryParams.append("costCenter", "All");
    } else {
      const costCenterIds = params.costCenterIds.map(id => id.toString().split('-')[0]).join('~'); // Convert array to comma-separated string
      queryParams.append("costCenter", costCenterIds);
    }
  }
  if (params.countOnly) queryParams.append("countOnly", "true")

  const response = await api.get(`${endpoints.ubt.selectUBT.getSelectUBTList}?${queryParams.toString()}`);
  return response.data;
};
